import { useState, type FormEvent, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, Plus } from 'lucide-react'
import {
  ssoSetupApi,
  type SsoLinkSummary,
  type SsoProvider,
  type SsoProviderInput,
  type SsoSetup,
} from '@/api/ssoSetupApi'
import { usePermission } from '@/lib/permissions'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { CopyButton } from '@/components/integrations/WebhooksPanel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const summaryText = ({ linked, failed }: Partial<SsoLinkSummary>) =>
  [
    linked !== undefined && `Linked ${linked} user${linked === 1 ? '' : 's'}.`,
    failed?.length && `Not linked: ${failed.map((f) => (f.error ? `${f.email} (${f.error})` : f.email)).join(', ')}.`,
  ]
    .filter(Boolean)
    .join(' ')

async function saveFile(file: 'metadata.xml' | 'certificate.pem' | 'certificate.cer') {
  const { blob, filename } = await ssoSetupApi.download(file)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * SSO setup (Configure → SSO, also `/sso-setup`). Customer admins (users.manage)
 * read the values, downloads, instructions and providers; Title21 super-admins
 * also manage the providers.
 */
export function SsoSetupPage() {
  const { isSuperAdmin } = usePermission()
  const queryClient = useQueryClient()
  const setup = useQuery({
    queryKey: ['sso-setup'],
    queryFn: ssoSetupApi.setup,
  })
  // /sso/providers is super-admin only; it feeds the edit form.
  const stored = useQuery({
    queryKey: ['sso-providers'],
    queryFn: ssoSetupApi.listProviders,
    enabled: isSuperAdmin,
  })
  const [editing, setEditing] = useState<SsoProvider | 'new' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sso-setup'] }),
      queryClient.invalidateQueries({ queryKey: ['sso-providers'] }),
    ])

  async function run<T>(action: () => Promise<T>, done: (result: T) => string) {
    setBusy(true)
    setNotice(null)
    setError(null)
    try {
      setNotice(done(await action()))
    } catch (err) {
      setError(apiErrorMessage(err, 'The change was refused.'))
    } finally {
      // Also after an error: a slow call can fail on the client but commit on the server.
      await refresh()
      setBusy(false)
    }
  }

  async function download(file: 'metadata.xml' | 'certificate.pem' | 'certificate.cer') {
    setError(null)
    try {
      await saveFile(file)
    } catch (err) {
      setError(apiErrorMessage(err, 'Download failed.'))
    }
  }

  if (setup.isLoading) return <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
  if (setup.isError || !setup.data) {
    return <p className='text-sm text-destructive'>{apiErrorMessage(setup.error, 'Could not load SSO setup.')}</p>
  }
  const s = setup.data

  return (
    <div className='flex flex-col gap-6'>
      <Card>
        <CardHeader className='flex-row items-start justify-between space-y-0'>
          <div className='space-y-1.5'>
            <CardTitle className='text-base'>Identity providers</CardTitle>
            <CardDescription>
              Each provider signs in only the users of its email domains. Give your users the sign-on URL, or add it as
              a tile in your portal. Run Test SSO in a private window: a successful test replaces the session in this
              browser.
            </CardDescription>
          </div>
          {isSuperAdmin && (
            <Button size='sm' onClick={() => setEditing('new')}>
              <Plus className='mr-1 h-4 w-4' /> Add provider
            </Button>
          )}
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {notice && (
            <p className='rounded-md bg-green-50 px-3 py-2 text-sm text-green-700' role='status'>
              {notice}
            </p>
          )}
          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}
          {s.providers.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No identity provider is connected yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sign-on URL</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.providers.map((p) => (
                  <TableRow key={p.name}>
                    <TableCell>
                      <div className='font-medium'>{p.displayName}</div>
                      <div className='font-mono text-xs text-muted-foreground'>{p.name}</div>
                    </TableCell>
                    <TableCell className='text-sm'>{p.emailDomains.join(', ')}</TableCell>
                    <TableCell>
                      <Badge variant={p.enabled ? 'default' : 'secondary'}>{p.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center gap-1'>
                        <span className='break-all font-mono text-xs'>{p.signOnUrl}</span>
                        <CopyButton value={p.signOnUrl} label='Copy sign-on URL' />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className='flex flex-wrap justify-end gap-1'>
                        {p.enabled && (
                          <Button size='sm' variant='outline' asChild>
                            {/* A new tab: a successful test replaces the session stored in this browser. */}
                            <a href={p.signOnUrl} target='_blank' rel='noreferrer'>
                              Test SSO
                            </a>
                          </Button>
                        )}
                        {isSuperAdmin && (
                          <>
                            <Button
                              size='sm'
                              variant='outline'
                              disabled={!stored.data}
                              onClick={() => setEditing(stored.data?.find((x) => x.name === p.name) ?? null)}
                            >
                              Edit
                            </Button>
                            <Button
                              size='sm'
                              variant='outline'
                              disabled={busy}
                              onClick={() =>
                                run(
                                  () =>
                                    p.enabled
                                      ? ssoSetupApi.disableProvider(p.name)
                                      : ssoSetupApi.enableProvider(p.name),
                                  () => `${p.displayName} ${p.enabled ? 'disabled' : 'enabled'}.`,
                                )
                              }
                            >
                              {p.enabled ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              size='sm'
                              variant='destructive'
                              disabled={busy || p.enabled}
                              title={p.enabled ? 'Disable the provider first' : undefined}
                              onClick={() => {
                                if (window.confirm(`Delete ${p.displayName}? Its users keep password login.`)) {
                                  void run(
                                    () => ssoSetupApi.deleteProvider(p.name),
                                    () => `${p.displayName} deleted.`,
                                  )
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!s.configured ? (
        <p className='text-sm text-muted-foreground'>SSO is not configured in this environment.</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Values to enter at your identity provider</CardTitle>
              <CardDescription>The same for every provider in this environment.</CardDescription>
            </CardHeader>
            <CardContent className='flex flex-col gap-3'>
              <Value label='Entity ID (Identifier, Audience URI)' value={s.entityId} />
              <Value label='Reply URL (ACS, single sign-on URL)' value={s.acsUrl} />
              <Value label='NameID format' value={s.nameIdFormat} />
              <p className='text-sm text-muted-foreground'>
                The NameID must be the user's work email: it is how smart-cgt matches the sign-in to the account. No
                other claims are needed.
              </p>
              <p className='text-sm text-muted-foreground'>
                Most identity providers (Entra ID, ADFS) import the SP metadata file directly, so you don't need to type
                these values. The signing certificate is only for providers that ask for one separately.
              </p>
              <div className='flex flex-wrap gap-2'>
                <Button variant='outline' size='sm' onClick={() => void download('metadata.xml')}>
                  <Download className='mr-1 h-4 w-4' /> SP metadata (XML)
                </Button>
                <Button variant='outline' size='sm' onClick={() => void download('certificate.pem')}>
                  <Download className='mr-1 h-4 w-4' /> Signing certificate (.pem)
                </Button>
                <Button variant='outline' size='sm' onClick={() => void download('certificate.cer')}>
                  <Download className='mr-1 h-4 w-4' /> Signing certificate (.cer)
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Instructions</CardTitle>
              <CardDescription>
                <ol className='mt-1 list-decimal space-y-1 pl-5'>
                  <li>Download the SP metadata (XML) above.</li>
                  <li>Create a SAML app for smart-cgt in your identity provider (steps per provider below).</li>
                  <li>
                    Send Title21 your identity provider's metadata URL (or metadata file), the email domains it signs in,
                    and one test user.
                  </li>
                  <li>When the provider appears in the list above, sign in with Test SSO in a private window.</li>
                </ol>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Instructions setup={s} />
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Rules for your users</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className='list-disc space-y-1 pl-5 text-sm'>
            <li>
              Every user must also exist in smart-cgt, created by an administrator. Users who are not in smart-cgt are
              refused, even when your identity provider lets them through.
            </li>
            <li>A user's email domain decides which identity provider signs them in.</li>
            <li>Users marked SSO-only can no longer sign in with a password. Other users can use either.</li>
            <li>Electronic signatures use a separate signing password, which SSO-only users set on their profile.</li>
            <li>
              Signing out of smart-cgt does not sign the user out of your identity provider. On shared computers, set
              your provider to ask for credentials at every sign-in (for example Entra sign-in frequency).
            </li>
          </ul>
        </CardContent>
      </Card>

      {editing && (
        <ProviderDialog
          provider={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
            // A save that errored on the client may still have committed.
            void refresh()
          }}
          onSaved={async (message) => {
            setEditing(null)
            setError(null)
            setNotice(message)
            await refresh()
          }}
        />
      )}
    </div>
  )
}

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className='text-sm font-medium'>{label}</p>
      <div className='flex items-center gap-1'>
        <span className='break-all font-mono text-xs'>{value}</span>
        <CopyButton value={value} />
      </div>
    </div>
  )
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className='list-decimal space-y-2 pl-5 text-sm'>{children}</ol>
}

function Code({ children }: { children: ReactNode }) {
  return <code className='break-all rounded bg-muted px-1 py-0.5 font-mono text-xs'>{children}</code>
}

function Instructions({ setup: s }: { setup: SsoSetup }) {
  // Each provider has its own sign-on URL, listed in the table above.
  const signOn = "your provider's sign-on URL from the Identity providers table"
  return (
    <Tabs defaultValue='entra'>
      <TabsList>
        <TabsTrigger value='entra'>Microsoft Entra ID</TabsTrigger>
        <TabsTrigger value='okta'>Okta</TabsTrigger>
        <TabsTrigger value='adfs'>ADFS</TabsTrigger>
        <TabsTrigger value='saml'>Generic SAML</TabsTrigger>
      </TabsList>
      <TabsContent value='entra' className='mt-4'>
        <Steps>
          <li>
            In the Microsoft Entra admin center open Enterprise applications → New application → Create your own
            application, and choose “Integrate any other application you don't find in the gallery”.
          </li>
          <li>Open Single sign-on and choose SAML.</li>
          <li>
            Under Basic SAML Configuration, upload the SP metadata file, or enter Identifier (Entity ID){' '}
            <Code>{s.entityId}</Code> and Reply URL <Code>{s.acsUrl}</Code>. Sign on URL is optional: {signOn}.
          </li>
          <li>
            Under Attributes &amp; Claims set Unique User Identifier (Name ID) to <Code>user.mail</Code> with format
            Email address. Other claims are not used.
          </li>
          <li>Under SAML Certificates copy the App Federation Metadata Url.</li>
          <li>Under Users and groups, assign the users or groups who may sign in.</li>
        </Steps>
      </TabsContent>
      <TabsContent value='okta' className='mt-4'>
        <Steps>
          <li>In the Okta Admin Console open Applications → Create App Integration and choose SAML 2.0.</li>
          <li>
            Set Single sign-on URL to <Code>{s.acsUrl}</Code> (keep “Use this for Recipient URL and Destination URL”
            checked) and Audience URI (SP Entity ID) to <Code>{s.entityId}</Code>.
          </li>
          <li>Set Name ID format to EmailAddress and Application username to Email.</li>
          <li>Finish, then on the Sign On tab copy the Metadata URL.</li>
          <li>On the Assignments tab, assign the users or groups who may sign in.</li>
        </Steps>
      </TabsContent>
      <TabsContent value='adfs' className='mt-4'>
        <Steps>
          <li>In AD FS Management open Relying Party Trusts → Add Relying Party Trust and choose Claims aware.</li>
          <li>
            Choose “Import data about the relying party from a file” and select the SP metadata file. Entered by hand:
            enable SAML 2.0 WebSSO with URL <Code>{s.acsUrl}</Code> and add the identifier <Code>{s.entityId}</Code>.
          </li>
          <li>Pick an access control policy that admits the users who may sign in.</li>
          <li>
            Edit Claim Issuance Policy → Add Rule → Send LDAP Attributes as Claims: E-Mail-Addresses to E-Mail Address.
          </li>
          <li>Add Rule → Transform an Incoming Claim: E-Mail Address to Name ID, outgoing format Email.</li>
          <li>
            Your metadata URL is{' '}
            <Code>https://&lt;your AD FS host&gt;/FederationMetadata/2007-06/FederationMetadata.xml</Code>.
          </li>
        </Steps>
      </TabsContent>
      <TabsContent value='saml' className='mt-4'>
        <Steps>
          <li>
            Create a SAML 2.0 service provider with Entity ID / Audience <Code>{s.entityId}</Code> and Assertion
            Consumer Service <Code>{s.acsUrl}</Code> (HTTP-POST binding). Or import the SP metadata file.
          </li>
          <li>
            Send NameID in format <Code>{s.nameIdFormat}</Code> with the user's work email as the value.
          </li>
          <li>
            Sign the response or assertion (SHA-256). Sign-in starts at {signOn}; IdP-initiated login is not supported.
          </li>
          <li>Assign the users or groups who may sign in.</li>
        </Steps>
      </TabsContent>
    </Tabs>
  )
}

const domainsFrom = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean)

function ProviderDialog({
  provider,
  onClose,
  onSaved,
}: {
  provider: SsoProvider | null
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [name, setName] = useState(provider?.name ?? '')
  const [displayName, setDisplayName] = useState(provider?.displayName ?? '')
  const [metadataUrl, setMetadataUrl] = useState(provider?.metadataUrl ?? '')
  const [metadataXml, setMetadataXml] = useState<string | null>(null)
  const [domains, setDomains] = useState(provider?.emailDomains.join(', ') ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const input: SsoProviderInput = {
      displayName,
      emailDomains: domainsFrom(domains),
    }
    // A new file wins; otherwise the URL.
    if (metadataXml) input.metadataXml = metadataXml
    else if (metadataUrl) input.metadataUrl = metadataUrl
    setSaving(true)
    try {
      if (provider) {
        const result = await ssoSetupApi.updateProvider(provider.name, input)
        onSaved([`${displayName} saved.`, summaryText(result ?? {})].filter(Boolean).join(' '))
      } else {
        const result = await ssoSetupApi.createProvider({ ...input, name })
        onSaved(`${displayName} added. ${summaryText(result)}`)
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'The provider could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  const needsMetadata = !provider && !metadataUrl && !metadataXml

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{provider ? `Edit ${provider.displayName}` : 'Add identity provider'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-3'>
          <Field
            label='Name'
            hint='Lowercase letters, digits and dashes (3–32). Used in the sign-on URL; cannot change.'
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={!!provider}
              pattern='[a-z0-9\-]{3,32}'
            />
          </Field>
          <Field label='Display name' hint='Shown on the sign-in page.'>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </Field>
          <Field label='SAML metadata URL' hint='Preferred: certificates then refresh on their own.'>
            <Input type='url' value={metadataUrl} onChange={(e) => setMetadataUrl(e.target.value)} />
          </Field>
          <Field label='or metadata XML file'>
            <Input
              type='file'
              accept='.xml,application/xml,text/xml'
              onChange={async (e) => setMetadataXml((await e.target.files?.[0]?.text()) ?? null)}
            />
          </Field>
          <Field label='Email domains' hint='Comma separated, e.g. montefiore.org. A domain belongs to one provider.'>
            <Input value={domains} onChange={(e) => setDomains(e.target.value)} required />
          </Field>
          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving || needsMetadata}>
              {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {provider ? 'Save' : 'Add provider'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className='flex flex-col gap-1.5'>
      <span className='text-sm font-medium'>{label}</span>
      {children}
      {hint && <span className='text-xs text-muted-foreground'>{hint}</span>}
    </label>
  )
}
